import React, { useState } from "react";
import { Tables, showToast } from "@deeptrue-frontend/ui";
import { UserType } from "pages/OrganizationAdmin/Users";
import RemoveModal from "components/PromptModal/PromptModal";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import Tooltip from "@material-ui/core/Tooltip";
import ModalWithTitle from "components/Modal/ModalWIthTitle";
import Forms from "components/Forms";
import { getOrganizationRoleNme } from "consts/auth";
import dayjs from "dayjs";
import api from "api";
import { FormValues } from "components/Forms/Organization/EditUser";
import { FormikHelpers } from "formik";
import styled from "styled-components/macro";
import { GroupType } from "pages/OrganizationAdmin/types";
import { OptionType } from "components/Fields/Search";
import RecentActorsIcon from "@material-ui/icons/RecentActors";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import CategoryIcon from "@material-ui/icons/Category";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { RouteComponentProps } from "react-router-dom";
import { useFormatMessage } from "components/IntlProvider";

const { withRouter } = RouterProvider;
const { TableRow, TableCell } = Tables;

const Role = styled.span`
  text-transform: capitalize;
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: #d1dffa;
  color: #353531;
  border-radius: 4px;
  padding: 0 0.5rem;
  margin: 2px;
`;

interface Props extends RouteComponentProps {
  userData: UserType;
  organizationCodename: string;
  refetchUsers: () => void;
}

const User = ({
  userData,
  organizationCodename,
  refetchUsers,
  history,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const t = useFormatMessage();

  const updateUser = (
    values: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) => {
    const formatedGroups = values.org_groups
      ? values.org_groups.map((option: OptionType) => option.value)
      : [];

    return api.resources.organization.users
      .updateUserInOrganization({
        organizationCodename: organizationCodename,
        userId: userData.id,
        data: {
          email: values.email,
          organization_role: Number(values.organization_role),
          org_groups: formatedGroups,
        },
      })
      .then(() => {
        setIsEditing(false);
        showToast({
          type: "success",
          title: t("organization.updated", "{name} updated", {
            name: t("organization.user", "User"),
          }),
        });
        refetchUsers();
      })
      .catch((err) => {
        setErrors(err.response.data);
      });
  };

  const updateUserActiveState = (isActive: boolean) => {
    return api.resources.organization.users
      .updateUserActiveState({
        organizationCodename,
        userId: userData.id,
        isActive: isActive,
      })
      .then(() => {
        showToast({
          type: "success",
          title: t("organization.updated", "{name} updated", {
            name: t("organization.user", "User"),
          }),
        });
        refetchUsers();
      });
  };

  const deleteUser = () => {
    return api.resources.organization.users
      .deleteeUserFromOrganization({
        organizationCodename,
        userId: userData.id,
      })
      .then(() => {
        showToast({
          type: "success",
          title: t("organization.deleted", "{name} deleted", {
            name: t("organization.user", "User"),
          }),
        });
        refetchUsers();
      });
  };

  return (
    <TableRow>
      <TableCell
        height="2rem"
        padding="0.5rem"
      >{`${userData.user.first_name} ${userData.user.last_name}`}</TableCell>
      <TableCell height="2rem" padding="0.5rem">
        {userData.user.email}
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        <Role>{getOrganizationRoleNme(userData.organization_role)}</Role>
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        {userData.user.org_groups &&
          userData.user.org_groups.map((group) => (
            <Pill key={group.name}>{group.name}</Pill>
          ))}
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        <span style={{ color: userData.is_active ? "black" : "red" }}>
          {userData.is_active
            ? t("organization.active", "Active")
            : t("organization.deactive", "Deactivated")}
        </span>
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        {userData.user.last_login
          ? dayjs(userData.user.last_login).format("DD-MM-YYYY HH:MM")
          : "-"}
      </TableCell>
      <TableCell height="2rem" padding="0.5rem">
        <Tooltip
          title={t("organization.editX", "Edit {name}", {
            name: t("organization.user", "user"),
          })}
        >
          <EditIcon
            style={{ cursor: "pointer", margin: "0 2px" }}
            onClick={() => setIsEditing(true)}
          />
        </Tooltip>
        <Tooltip title={t("organization.groups", "Groups")}>
          <RecentActorsIcon
            style={{ cursor: "pointer", margin: "0 2px" }}
            onClick={() =>
              history.push(
                `/organization/users/${userData.id}/shared?tab=groups`
              )
            }
          />
        </Tooltip>
        <Tooltip
          title={t("organization.shared", "Shared {name}", {
            name: t("organization.projects", "Projects"),
          })}
        >
          <AccountTreeIcon
            style={{ cursor: "pointer", margin: "0 2px" }}
            onClick={() =>
              history.push(
                `/organization/users/${userData.id}/shared?tab=projects`
              )
            }
          />
        </Tooltip>
        <Tooltip
          title={t("organization.shared", "Shared {name}", {
            name: t("organization.products", "Products"),
          })}
        >
          <CategoryIcon
            style={{ cursor: "pointer", margin: "0 2px" }}
            onClick={() =>
              history.push(
                `/organization/users/${userData.id}/shared?tab=products`
              )
            }
          />
        </Tooltip>
        <Tooltip
          title={t("organization.delete", "Delete {name}", {
            name: t("organization.user", "user"),
          })}
        >
          <span style={{ cursor: "pointer" }}>
            <RemoveModal
              onConfirm={deleteUser}
              renderer={({ handleOpen }: { handleOpen: any }) => (
                <DeleteIcon onClick={handleOpen} />
              )}
            >
              <>
                {t(
                  "organization.confirmUserDelete",
                  "Are you sure you want to remove user {email}",
                  {
                    email: userData.user.email,
                  }
                )}
              </>
            </RemoveModal>
          </span>
        </Tooltip>
        <Tooltip
          title={t("organization.activeDeactive", "Active/Deactive User")}
        >
          <span style={{ cursor: "pointer" }}>
            <RemoveModal
              onConfirm={() => updateUserActiveState(!userData.is_active)}
              renderer={({ handleOpen }: { handleOpen: any }) => (
                <PowerSettingsNewIcon onClick={handleOpen} />
              )}
              isPositive={!userData.is_active && "Deactivated"}
            >
              <>
                {t(
                  "organization.activeDeactiveUser",
                  "Are you sure you want to {action} user?",
                  {
                    action: userData.is_active ? "Deactivate" : "Activate",
                  }
                )}
              </>
            </RemoveModal>
          </span>
        </Tooltip>
      </TableCell>
      <ModalWithTitle
        title={t("organization.form.editUser", "Edit user")}
        open={isEditing}
        onClose={() => setIsEditing(false)}
      >
        <Forms.EditUserInOrganization
          onSubmit={updateUser}
          closeModal={() => setIsEditing(false)}
          organizationCodename={organizationCodename}
          initialValues={{
            email: userData.user.email,
            organization_role: userData.organization_role.toString(),
            org_groups: userData.user.org_groups.map((group: GroupType) => ({
              value: group.id,
              label: group.name,
            })),
          }}
        />
      </ModalWithTitle>
    </TableRow>
  );
};

export default withRouter(User);
